import { useEffect, useId, useState } from 'react'

const inputCls =
  'rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] text-slate-700 ' +
  'focus:border-amber-400 focus:outline-none'

function useDriverRoster(path: string): string[] {
  const [drivers, setDrivers] = useState<string[]>([])
  useEffect(() => {
    let live = true
    fetch(path)
      .then((r) => (r.ok ? (r.json() as Promise<{ drivers?: string[] }>) : null))
      .then((d) => {
        if (!live || !d?.drivers) return
        setDrivers(d.drivers)
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [path])
  return drivers
}

/**
 * Type-to-search driver picker, same shape as VehicleSearchField. Default roster is
 * Ultratech's v_vehicle_driver_spell (real TIMS-scan attribution, not
 * vehicle.driver_name -- that column is empty fleet-wide) via /api/reports/
 * driver-roster -- rosterPath overrides this for a different site's own driver
 * source (e.g. MBMT's /api/mbmt/driver-roster, live TIMS duty cache; Nagpur's
 * /api/bus/driver-roster?spv=NAGPUR, EDMS-sourced site_duty_trip -- .claude/work/
 * SPEC.md addendum). Every existing caller keeps today's exact behavior unchanged
 * (the default IS the old hardcoded path).
 */
export function DriverSearchField({
  value,
  onChange,
  label = 'driver',
  rosterPath = '/api/reports/driver-roster',
}: {
  value: string
  onChange: (driverName: string) => void
  label?: string
  rosterPath?: string
}): JSX.Element {
  const drivers = useDriverRoster(rosterPath)
  // useId(), not a shared string literal (adversarial review, LOW) -- three distinct
  // rosterPath callers now exist (Ultratech/MBMT/Nagpur); a shared hardcoded id would
  // collide if two instances ever rendered on the same page, same fix VehicleSearchField
  // already applies to its own datalist.
  const listId = useId()

  return (
    <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
      {label}
      <span className="flex items-center gap-1">
        <input
          type="text"
          list={listId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="all drivers"
          className={`${inputCls} w-[160px] normal-case`}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="clear driver filter"
            title="clear"
            className="rounded px-1 py-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            ×
          </button>
        )}
      </span>
      <datalist id={listId}>
        {drivers.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>
    </label>
  )
}
